import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  useGetMentorsPooling,
  useMentorsByContext,
  useQueryClassification,
  useSearchMentors,
  useSearchTitles,
  useUniversalSearch,
} from '../../hooks/apiHooks/searchHooks'
import { useMentorsState } from '../../hooks/storeHooks/useMentors'
import { useSession } from '../../hooks/storeHooks/useSessionState'
import { MentorBase, MentorWithContext } from '../../types/mentor'
import { analytics } from '../../utils/analytics'

export interface Title {
  clickbaitHeader: string
  imageUrl: string | null
  prompt: string | null
  id: string
  title: string
  userQuery: string
  mentor?: MentorWithContext | null
}

const PAGE_SIZE = 18
const TITLES_SIZE = 3

export const useMentorsSearch = () => {
  const queryClient = useQueryClient()
  const [mentors, setMentors] = useState<MentorBase[] | null>()

  const { secondsBalance, isSubsDataLoading } = useSession()
  const navigate = useNavigate()

  const {
    titles: titlesState,
    setTitles: setTitlesState,
    setIsSearchingTitles,
  } = useMentorsState()
  const [searchParams] = useSearchParams()
  const query = searchParams.get('query')?.trim() || ''
  const { data: queryClassification, isLoading: isQueryClassificationLoading } =
    useQueryClassification(encodeURIComponent(query))

  useEffect(() => {
    if (queryClassification?.classification) {
      analytics.logEvent('aa_question_asked', {
        user_intent: queryClassification?.classification,
      })
    }
  }, [queryClassification])

  const isSearchQuestion = useMemo(
    () =>
      !isQueryClassificationLoading &&
      queryClassification?.classification === 'specific_question',
    [queryClassification, isQueryClassificationLoading],
  )

  const isApiEnabled = useMemo(() => {
    return !titlesState[0].mentor?.mentorId && isSearchQuestion
  }, [titlesState, isSearchQuestion])

  const { data: searchMentorsData } = useSearchMentors(
    'search-mentors-page',
    {
      search: query,
      size: PAGE_SIZE,
    },
    !isSearchQuestion && !isQueryClassificationLoading,
    false,
  )

  const {
    data: contextData,
    isPending: isSearchTitlesPending,
    mutate: getSearchTitles,
    reset: resetTitles,
  } = useSearchTitles()

  const { data: universalSearch, isLoading: isUniversalSearchLoading } =
    useUniversalSearch({
      search: query,
      size: PAGE_SIZE,
      isEnabled: !!secondsBalance && !isSubsDataLoading && isSearchQuestion,
    })

  const { mutateAsync: getMentorsPooling } = useGetMentorsPooling()

  useEffect(() => {
    if (!secondsBalance && !isSubsDataLoading) {
      navigate('/', {
        state: {
          showOutOfMinutesModal: true,
        },
      })
    }
  }, [secondsBalance, isSubsDataLoading])

  useEffect(() => {
    if (query && secondsBalance && !isSubsDataLoading) {
      if (isApiEnabled) {
        getSearchTitles(query)
      }
    } else {
      resetTitles()
    }
  }, [
    query,
    isApiEnabled,
    getSearchTitles,
    resetTitles,
    secondsBalance,
    isSubsDataLoading,
  ])

  useEffect(() => {
    if (isUniversalSearchLoading) {
      setMentors(null)
      return
    }
    setMentors(universalSearch)
  }, [universalSearch, setMentors, isUniversalSearchLoading])

  useEffect(() => {
    if (contextData?.contexts) {
      setTitlesState(contextData.contexts)
    }
  }, [contextData?.contexts])

  const mentorIds =
    [...titlesState].filter(title => title.id).map(title => title.id) || []

  const mentorsData = useMentorsByContext({
    contextIds: mentorIds,
    scopeId: contextData?.scopeId || '',
    quantity: TITLES_SIZE,
    isApiEnabled,
  })

  useEffect(() => {
    if (
      !titlesState.length ||
      !mentorsData?.length ||
      mentorsData.some(mentor => !mentor)
    ) {
      return
    }

    const newState = [...titlesState].map(title => {
      const mentor =
        mentorsData.find(m => m?.userContextId === title.id) || null
      return { ...title, mentor }
    })

    setTitlesState(newState)
  }, [titlesState.length, mentorsData])

  useEffect(() => {
    if (titlesState.some(title => !title.id)) return

    const generatedMentorList = [...titlesState].filter(
      title => title.mentor?.mentorType === 'GENERATED',
    )
    if (!generatedMentorList.length) return

    const updateMentors = async () => {
      const { items } = await getMentorsPooling(
        generatedMentorList.map(title => title.mentor?.mentorId || ''),
      )

      const newState = [...titlesState].map(title => {
        const updatedMentor =
          items.find(
            (mentor: Title['mentor']) =>
              mentor?.mentorId === title.mentor?.mentorId,
          ) || title.mentor
        return updatedMentor ? { ...title, mentor: updatedMentor } : title
      })

      setTitlesState(newState)
    }

    const interval = setInterval(() => {
      void updateMentors()
    }, 5000)
    return () => clearInterval(interval)
  }, [titlesState, getMentorsPooling, setTitlesState])

  useEffect(() => {
    if (!mentors || !mentors.length || !isSearchQuestion) return

    const ids: string[] = mentors
      .filter(mentor => mentor.status !== 'Completed')
      .map(mentor => mentor.mentorId || '')

    if (!ids.length) return

    const updateMentors = async () => {
      const { items } = await getMentorsPooling(ids)
      setMentors((prevMentors: MentorBase[] | null | undefined) => {
        if (!prevMentors) return []
        const updatedMentors = prevMentors.map(mentor => {
          const updatedMentor = items.find(m => m.mentorId === mentor.mentorId)
          return updatedMentor
            ? { ...mentor, ...updatedMentor, status: 'Completed' }
            : mentor
        })
        return updatedMentors
      })
    }

    const interval = setInterval(() => {
      void updateMentors()
    }, 5000)
    return () => clearInterval(interval)
  }, [mentors, isSearchQuestion, setMentors])

  useEffect(() => {
    setIsSearchingTitles(
      isSearchTitlesPending ||
        isUniversalSearchLoading ||
        isQueryClassificationLoading,
    )
  }, [
    isSearchTitlesPending,
    isUniversalSearchLoading,
    isQueryClassificationLoading,
    setIsSearchingTitles,
  ])

  useEffect(() => {
    if (isQueryClassificationLoading) {
      void queryClient.removeQueries({
        queryKey: [
          'search-mentors-page',
          'universal-search',
          'query-classification-search',
        ],
        exact: false,
      })
      setTitlesState(new Array(3).fill({}) as Title[])
    }
  }, [isQueryClassificationLoading])

  return {
    titles: titlesState,
    mentors: isSearchQuestion ? mentors || null : searchMentorsData || null,
    isSearchQuestion,
    isLoading: isQueryClassificationLoading,
    query,
  }
}
